import { Spinner } from "@/components/ui/spinner";
import { useState, useEffect } from "react";
import { useBudgetStore } from "../store/useBudgetStore";
import AmountInput from "@/components/AmountInput";

export default function OverallBudgetCard() {
  const { budget, saving, error, upsertBudget } = useBudgetStore();
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (budget) setInputValue(String(budget.overallAmount));
  }, [budget]);

  async function handleSave() {
    const amount = parseFloat(inputValue);
    if (!inputValue || isNaN(amount) || amount <= 0) {
      setInputError("Overall budget must be greater than 0");
      return;
    }

    const categorySum = (budget?.categoryBudgets ?? []).reduce(
      (s, c) => s + c.amount,
      0,
    );
    if (categorySum > amount) {
      const excess = categorySum - amount;
      setInputError(
        `Overall budget is below your allocated category budgets (฿${excess.toLocaleString("th-TH", { minimumFractionDigits: 0 })})`,
      );
      return;
    }

    setInputError(null);
    try {
      await upsertBudget(amount);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error shown from store
    }
  }

  function handleChange(value: string) {
    setInputValue(value);
    if (inputError) setInputError(null);
    if (saved) setSaved(false);
  }

  const buffer = budget?.unallocatedBuffer ?? null;

  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-6 shadow-card">
      <h2 className="text-[15px] font-semibold text-foreground mb-1">
        Overall Monthly Budget
      </h2>
      <p className="text-[13px] text-muted-foreground mb-5">
        Set a spending ceiling for this month.
      </p>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-muted-foreground flex-shrink-0">
            ฿
          </span>
          <AmountInput
            value={inputValue}
            onChange={handleChange}
            placeholder="e.g. 30000"
            className={`flex-1 rounded-xl border px-4 py-2.5 text-[14px] bg-background text-foreground outline-none transition-colors [font-variant-numeric:tabular-nums] ${
              inputError
                ? "border-destructive focus:ring-1 focus:ring-destructive"
                : "border-border focus:border-primary focus:ring-1 focus:ring-primary"
            }`}
          />
        </div>

        {inputError && (
          <p className="text-[12px] text-destructive">{inputError}</p>
        )}
        {error && !inputError && (
          <p className="text-[12px] text-destructive">{error}</p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center gap-1.5 self-start px-5 py-2 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {saving && <Spinner className="size-3.5" />}
          {saving ? "Saving…" : saved ? "Saved!" : "Save"}
        </button>
      </div>

      {budget && (
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-[12px] text-muted-foreground mb-1">
            Current budget
          </p>
          <p className="text-[22px] font-bold text-foreground [font-variant-numeric:tabular-nums]">
            ฿
            {budget.overallAmount.toLocaleString("th-TH", {
              minimumFractionDigits: 0,
            })}
          </p>

          {buffer !== null && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">
                Unallocated buffer
              </span>
              <span
                className={`text-[13px] font-semibold [font-variant-numeric:tabular-nums] ${
                  buffer < 0 ? "text-destructive" : "text-emerald-500"
                }`}
              >
                ฿{buffer.toLocaleString("th-TH", { minimumFractionDigits: 0 })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
